import * as React from 'react';
import { useContext } from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import { LinkIcon } from '@phosphor-icons/react';

import { RouterNavRoutes } from '@/components/sidenav/nav-from-router';
import { TeamSwitcher } from '@/components/sidenav/team-switcher';
import { NavUser } from '@/components/sidenav/nav-user';
import { ViewModeToggle } from '@/components/sidenav/view-mode-toggle';
import { HelpMenuSection } from '@/components/sidenav/help-menu-section';
import { ScrollShadow } from '@/components/common/ScrollShadow';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarTrigger,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from '@/components/ui/sidebar';
import { AuthContext } from '@/context/auth.context';
// Demo mode (throwaway): hide the Help menu while a demo is running.
import { isDemoMode } from '@/demo/core/demo-mode';
import { T } from 'gt-react';

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  brandName?: string;
}

export function AppSidebar({ brandName = 'App', ...props }: AppSidebarProps) {
  const { currentUser } = useContext(AuthContext);
  const isRestrictedUser = currentUser?.isRestrictedUser ?? false;

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <div className="flex items-center justify-between group-data-[collapsible=icon]:justify-center">
          <TeamSwitcher
            brandName={brandName}
            className="group-data-[collapsible=icon]:hidden"
          />
          <div className="flex items-center gap-1">
            {!isRestrictedUser && (
              <span className="group-data-[collapsible=icon]:hidden">
                <ViewModeToggle />
              </span>
            )}
            <SidebarTrigger />
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent className="overflow-hidden">
        <ScrollShadow
          className="flex-1 group-data-[collapsible=icon]:overflow-hidden"
          viewportClassName="group-data-[collapsible=icon]:overflow-x-hidden group-data-[collapsible=icon]:[scrollbar-width:none]"
        >
          {isRestrictedUser ? (
            <SidebarGroup>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Shared Links">
                    <RouterLink to="/shared-links">
                      <LinkIcon className="h-4 w-4" />
                      <span>
                        <T context="navigation link to shared links">
                          Shared Links
                        </T>
                      </span>
                    </RouterLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>
          ) : (
            <RouterNavRoutes />
          )}
        </ScrollShadow>
      </SidebarContent>
      <SidebarFooter>
        {/* Demo mode (throwaway): hide the Help menu so the demo sidebar reads clean. */}
        {!isRestrictedUser && !isDemoMode() && <HelpMenuSection />}
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
